<template>
  <div
    v-if="isViewingDifferentCompany && !isLoadingCompanies && legacyLastErrorStore.loading === 0"
  >
    <!-- Collapsed FAB -->
    <v-btn
      v-if="!isExpanded"
      class="company-indicator-fab"
      color="info"
      icon
      size="large"
      elevation="6"
      @click="isExpanded = true"
    >
      <v-icon>mdi-swap-horizontal</v-icon>
    </v-btn>

    <!-- Expanded Card -->
    <v-card v-else class="company-indicator-card" elevation="8">
      <v-card-text class="pa-4">
        <div class="header-row mb-3">
          <v-icon size="20" color="info" class="mr-2">mdi-swap-horizontal</v-icon>
          <span class="header-text">Viewing Different Company</span>
          <v-btn icon size="small" variant="text" @click="isExpanded = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <div class="mb-2 text-xl">
          {{ companiesStore.viewingCompanyName }}
        </div>
        <div v-if="formattedRole" class="text-grey-darken-1 mb-4 text-sm">
          Acting as: <span class="font-weight-medium">{{ formattedRole }}</span>
        </div>

        <v-autocomplete
          v-model="selectedCompany"
          hideDetails
          variant="outlined"
          density="compact"
          label="Switch to another company"
          persistentPlaceholder
          :items="formattedAccessibleCompanies"
          :loading="isLoadingCompanies"
          clearable
          returnObject
          itemTitle="displayName"
          itemValue="slug"
          :autoSelectFirst="false"
          class="mb-3"
          @update:modelValue="onCompanyChange"
        >
          <template #no-data>
            <div class="text-grey-darken-1 px-4 py-2 text-sm">
              {{ isLoadingCompanies ? "Loading companies..." : "No other companies available" }}
            </div>
          </template>
        </v-autocomplete>

        <v-btn color="primary" variant="flat" block @click="clearContext">
          Return to Your Company
        </v-btn>
      </v-card-text>
    </v-card>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useCompaniesStore } from "@/pinia/companiesStore";
import { useUserStore } from "@/pinia/userStore";
import { useLegacyLastErrorStore } from "@/pinia/legacyLastErrorStore";
import StringUtils from "@/utils/StringUtils";

const companiesStore = useCompaniesStore();
const userStore = useUserStore();
const legacyLastErrorStore = useLegacyLastErrorStore();

const isExpanded = ref(false);
const selectedCompany = ref(null);
const isLoadingCompanies = ref(false);

const isViewingDifferentCompany = computed(() => {
  // Don't show indicator until user data is loaded
  if (!userStore.currUser?.company_id) return false;

  const userCompanySlug = userStore.currUser.company_id; // Backend returns slug in company_id field
  return companiesStore.viewingCompanySlug && companiesStore.viewingCompanySlug !== userCompanySlug;
});

const formattedRole = computed(() => {
  return companiesStore.viewingCompanyRole
    ? StringUtils.titleize(companiesStore.viewingCompanyRole)
    : "";
});

const formattedAccessibleCompanies = computed(() => {
  return (companiesStore.accessibleCompanies || []).map(company => ({
    ...company,
    displayName: company.acting_role
      ? `${company.name} (${StringUtils.titleize(company.acting_role)})`
      : company.name,
  }));
});

const onCompanyChange = newCompany => {
  if (newCompany?.slug) {
    // Only set viewing context if it's a DIFFERENT company than user's own
    if (newCompany.slug === userStore.currUser?.company_id) {
      // Selected their own company - clear the context
      companiesStore.clearViewingCompany();
    } else {
      // Selected a different company - set the context with slug and role
      companiesStore.setViewingCompany({
        slug: newCompany.slug,
        name: newCompany.name,
        role: newCompany.acting_role,
      });
    }

    // Redirect to root to load apps from new company context
    window.location.href = "/";
  }
};

const clearContext = () => {
  companiesStore.clearViewingCompany();
  isExpanded.value = false;
  window.location.href = "/";
};

// Watch for when user data becomes available and load companies
watch(
  () => userStore.currUser?.id,
  async newUserId => {
    if (newUserId && userStore.currUser?.has_multi_company_access) {
      isLoadingCompanies.value = true;
      try {
        await companiesStore.getAccessibleCompanies(newUserId);
      } finally {
        isLoadingCompanies.value = false;
      }
    }
  },
  { immediate: true },
);
</script>

<style scoped>
.company-indicator-fab {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
}

.company-indicator-card {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
  width: 380px;
  border-radius: 12px;
  background: white;
  border: 2px solid #e0e0e0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: nowrap;
}

.header-text {
  flex: 1;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.4;
}
</style>
